import React from 'react';
import {
  FaClipboardList,
  FaProjectDiagram,
  FaTable,
  FaSitemap,
  FaTasks,
  FaExclamationTriangle,
  FaPlay,
  FaCalculator,
  FaLayerGroup,
  FaCheckDouble,
  FaBolt,
} from 'react-icons/fa';
import { Button, Spinner } from './Primitives';
import { REGENERATION_MODE } from '../utils/constants';

function StatRow({ icon: Icon, label, value, tone = 'default' }) {
  const tones = {
    default: 'text-gray-700 dark:text-gray-200',
    warn: 'text-amber-600 dark:text-amber-400',
    ok: 'text-emerald-600 dark:text-emerald-400',
    danger: 'text-red-600 dark:text-red-400',
  };
  return (
    <div className="flex items-center justify-between py-2">
      <span className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Icon size={13} className="text-gray-400" />
        {label}
      </span>
      <span className={`text-sm font-semibold tabular-nums ${tones[tone]}`}>{value}</span>
    </div>
  );
}

/** Selettore modalità di rigenerazione: scelta esplicita, non checkbox ambigua. */
function RegenerationModeControl({ mode, onChange }) {
  const options = [
    {
      id: REGENERATION_MODE.OUTDATED_ONLY,
      label: 'Genera solo ciò che non è aggiornato',
      icon: FaCheckDouble,
    },
    {
      id: REGENERATION_MODE.ALL,
      label: 'Forza rigenerazione completa',
      icon: FaBolt,
    },
  ];
  return (
    <fieldset className="mt-4">
      <legend className="mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-400">
        Modalità di rigenerazione
      </legend>
      <div className="space-y-1.5">
        {options.map((opt) => {
          const active = mode === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onChange(opt.id)}
              aria-pressed={active}
              className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                active
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-gray-300 text-gray-600 hover:border-primary/60 dark:border-gray-600 dark:text-gray-300'
              }`}
            >
              <opt.icon size={13} className="shrink-0" />
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-[11px] leading-snug text-gray-400 dark:text-gray-500">
        In “solo aggiornamenti” il backend rivalida la copertura e salta le
        combinazioni già aggiornate.
      </p>
    </fieldset>
  );
}

/**
 * Riepilogo sticky su desktop: selezione, copertura del piano e azioni.
 */
export default function SummaryPanel({
  macroCount,
  tableCount,
  skeletonCount,
  plan,
  incompatibleCount,
  regenerationMode,
  onSetRegenerationMode,
  onBuildPlan,
  onStartBatch,
  onForceAll,
  planLoading,
  starting,
  canPlan,
  canStart,
  allUpToDate,
  logLevel,
  onSetLogLevel,
  logLevels,
}) {
  const incompat = incompatibleCount ?? 0;
  const totalCombinations = plan?.totalCombinations;
  const toExecute = plan?.expectedExecutions;
  const skipped = plan?.skippedUpToDate;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-2">
        <FaClipboardList className="text-primary" size={15} />
        <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">Riepilogo</h2>
      </div>

      <div className="mt-3 divide-y divide-gray-100 dark:divide-gray-700">
        <StatRow icon={FaProjectDiagram} label="Macrofunzioni" value={macroCount} />
        <StatRow icon={FaTable} label="Tabelle distinte" value={tableCount} />
        <StatRow icon={FaSitemap} label="Scheletri" value={skeletonCount} />
        <StatRow
          icon={FaLayerGroup}
          label="Combinazioni totali"
          value={totalCombinations ?? '—'}
        />
        <StatRow
          icon={FaTasks}
          label="Da eseguire"
          value={toExecute ?? '—'}
          tone={toExecute > 0 ? 'default' : 'ok'}
        />
        <StatRow
          icon={FaCheckDouble}
          label="Già aggiornate"
          value={skipped ?? '—'}
          tone={skipped > 0 ? 'ok' : 'default'}
        />
        <StatRow
          icon={FaExclamationTriangle}
          label="Incompatibilità"
          value={incompat}
          tone={incompat > 0 ? 'warn' : 'default'}
        />
      </div>

      <RegenerationModeControl mode={regenerationMode} onChange={onSetRegenerationMode} />

      {/* Livello di log */}
      <div className="mt-4">
        <label
          htmlFor="gb-loglevel"
          className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400"
        >
          Livello di log
        </label>
        <select
          id="gb-loglevel"
          value={logLevel}
          onChange={(e) => onSetLogLevel(e.target.value)}
          className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        >
          {logLevels.map((lvl) => (
            <option key={lvl} value={lvl}>
              {lvl}
            </option>
          ))}
        </select>
      </div>

      {/* Stato positivo: tutto già aggiornato */}
      {allUpToDate && (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-800/60 dark:bg-emerald-900/20">
          <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
            <FaCheckDouble size={13} /> Tutto aggiornato
          </div>
          <p className="mt-1 text-xs text-emerald-700/80 dark:text-emerald-300/80">
            Tutte le combinazioni per gli scheletri selezionati risultano già
            aggiornate. Puoi comunque forzare una rigenerazione completa.
          </p>
          <Button
            variant="secondary"
            icon={FaBolt}
            onClick={onForceAll}
            className="mt-2 w-full !py-1.5 text-xs"
          >
            Forza rigenerazione completa
          </Button>
        </div>
      )}

      <div className="mt-5 space-y-2">
        <Button
          onClick={onBuildPlan}
          disabled={!canPlan || planLoading}
          icon={planLoading ? undefined : FaCalculator}
          variant="secondary"
          className="w-full"
        >
          {planLoading ? <Spinner className="h-4 w-4" /> : null}
          {planLoading ? 'Calcolo piano…' : 'Calcola piano'}
        </Button>

        <Button
          onClick={onStartBatch}
          disabled={!canStart || starting}
          icon={starting ? undefined : FaPlay}
          className="w-full"
        >
          {starting ? <Spinner className="h-4 w-4" /> : null}
          {starting ? 'Avvio…' : 'Avvia batch'}
        </Button>
      </div>

      {!canStart && plan && incompat > 0 && (
        <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">
          Risolvi le incompatibilità o gli errori bloccanti prima di avviare.
        </p>
      )}
    </div>
  );
}
